import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  AuditLogsFilters,
  AuditLogsFiltersSection,
} from '../components/features/AuditLogsManagement/AuditLogsFiltersSection';
import { ExportDropdown } from '../components/features/MonitoringAuditLogging/components/ExportDropdown/ExportDropdown';
import { SearchSection } from '../components/features/SearchSection/SearchSection';
import { AuditLogsTable } from '../components/tables/AuditLogsTable/AuditLogsTable';
import { SideDrawer } from '../components/ui/SideDrawer/SideDrawer';
import { PATHS } from '../constants/Paths.constants';
import { useAppSelector } from '../hooks/redux';
import { AppLayout } from '../layout/AppLayout';
import { DashboardLayout } from '../layout/DashboardLayout';
import {
  downloadTamperEvidenceReport,
  exportOrganizationLogsCSV,
  exportOrganizationLogsJSON,
  exportOrganizationLogsPDF,
  getAuditLogs,
} from '../services/SystemLoggingService';
import {
  selectIsPlatformAdmin,
  selectIsSuperAdmin,
} from '../store/selectors/authSelectors';
import { LogEntry } from '../types/SystemLogging.types';
import { getAuditLogDrawerContent } from '../components/features/AuditLogsManagement/AuditLogDrawerContent';
import { triggerBlobDownload } from '../utils/File.util';
import { splitUserFilterValues } from '../utils/LogUserFilter.util';
import { toEndOfDayISOString, toStartOfDayISOString } from '../utils/Date.util';

const PAGE_SIZE = 20;

const INITIAL_FILTERS: AuditLogsFilters = {
  types: [],
  userIds: [],
  dateRange: null,
};

type AuditLogExportOption = 'csv' | 'json' | 'pdf' | 'tamper-evidence';

export function AuditLogPage() {
  const isPlatformAdmin = useAppSelector(selectIsPlatformAdmin);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [sorting, setSorting] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' }>({
    sortBy: 'date',
    sortOrder: 'desc',
  });

  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedEntry, setSelectedEntry] = useState<LogEntry | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const hasMore = entries.length < total;

  // `requestIdRef` marks each fetch with an id and discards a response whose
  // id is no longer the latest one in flight when it resolves - covers both
  // a filter change firing while a "load more" batch is still in flight, and
  // "load more" clicked twice in quick succession, the same way the
  // per-AI-system Logs table (MonitoringAuditLogging) avoids a stale batch
  // landing out of order.
  const requestIdRef = useRef(0);

  const fetchPage = async (offset: number) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    const { userIds, githubUserNames } = splitUserFilterValues(filters.userIds);
    try {
      const response = await getAuditLogs({
        types: filters.types.length > 0 ? filters.types : undefined,
        userIds: userIds.length > 0 ? userIds : undefined,
        githubUserNames: githubUserNames.length > 0 ? githubUserNames : undefined,
        search: search || undefined,
        startDate: filters.dateRange?.startDate
          ? toStartOfDayISOString(filters.dateRange.startDate)
          : undefined,
        endDate: filters.dateRange?.endDate
          ? toEndOfDayISOString(filters.dateRange.endDate)
          : undefined,
        limit: PAGE_SIZE,
        offset,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      });
      if (requestIdRef.current !== requestId) return;
      setEntries((prev) => (offset === 0 ? response.items : [...prev, ...response.items]));
      setTotal(response.total);
    } catch {
      if (requestIdRef.current !== requestId) return;
      setEntries([]);
      setTotal(0);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    fetchPage(entries.length);
  };

  useEffect(() => {
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters, sorting]);

  const handleSort = (sortKey: string) => {
    setSorting((prev) =>
      prev.sortBy === sortKey
        ? { sortBy: sortKey, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' }
        : { sortBy: sortKey, sortOrder: 'asc' }
    );
  };

  const handleRowClick = (entry: LogEntry) => {
    setSelectedEntry(entry);
    setIsDrawerOpen(true);
  };

  const handleExport = async (option: AuditLogExportOption) => {
    const dateSuffix = new Date().toISOString().split('T')[0];

    try {
      if (option === 'tamper-evidence') {
        const download = await downloadTamperEvidenceReport();
        triggerBlobDownload(
          download.blob,
          download.filename || `tamper-evidence-report-${dateSuffix}.pdf`
        );
        return;
      }

      const { userIds, githubUserNames } = splitUserFilterValues(filters.userIds);
      const exportParams = {
        types: filters.types.length > 0 ? filters.types : undefined,
        userIds: userIds.length > 0 ? userIds : undefined,
        githubUserNames: githubUserNames.length > 0 ? githubUserNames : undefined,
        search: search || undefined,
        startDate: filters.dateRange?.startDate
          ? toStartOfDayISOString(filters.dateRange.startDate)
          : undefined,
        endDate: filters.dateRange?.endDate
          ? toEndOfDayISOString(filters.dateRange.endDate)
          : undefined,
      };

      const exportFns = {
        csv: exportOrganizationLogsCSV,
        json: exportOrganizationLogsJSON,
        pdf: exportOrganizationLogsPDF,
      };
      const download = await exportFns[option](exportParams);

      triggerBlobDownload(
        download.blob,
        download.filename || `audit-log-${dateSuffix}.${option}`
      );
    } catch (error) {
      console.error(`Failed to download ${option}:`, error);
    }
  };

  const exportOptions = [
    { value: 'csv' as const, label: 'CSV' },
    { value: 'json' as const, label: 'JSON' },
    { value: 'pdf' as const, label: 'PDF' },
  ];

  const reportsSection = isSuperAdmin
    ? {
        label: 'Reports',
        options: [{ value: 'tamper-evidence' as const, label: 'Tamper-Evidence Report' }],
      }
    : undefined;

  if (!isPlatformAdmin && !isSuperAdmin) {
    return <Navigate to={PATHS.ACCESS_DENIED} replace />;
  }

  return (
    <AppLayout>
      <DashboardLayout title="Audit Log">
        <div className="w-full flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <SearchSection
              searchValue={search}
              onSearchChange={setSearch}
              placeholder="Search by keyword"
            />

            <div className="flex items-center gap-2 flex-shrink-0">
              <ExportDropdown
                onExport={handleExport}
                options={exportOptions}
                secondarySection={reportsSection}
                label="Download Log"
              />
            </div>
          </div>

          <AuditLogsFiltersSection
            filters={filters}
            onFiltersChange={setFilters}
            isSuperAdmin={isSuperAdmin}
          />

          <AuditLogsTable
            entries={entries}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onRowClick={handleRowClick}
            sortBy={sorting.sortBy}
            sortOrder={sorting.sortOrder}
            onSort={handleSort}
          />
        </div>

        {selectedEntry && (
          <SideDrawer
            isOpen={isDrawerOpen}
            onClose={() => {
              setIsDrawerOpen(false);
              setSelectedEntry(null);
            }}
            title={selectedEntry.action}
            listItems={getAuditLogDrawerContent(selectedEntry)}
          />
        )}
      </DashboardLayout>
    </AppLayout>
  );
}
